import React, { useState, useEffect, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

const IGNORED_BROWSER_ERROR_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
];

const isIgnoredBrowserError = (message: unknown) => {
  const normalizedMessage = message instanceof Error ? message.message : String(message);
  return IGNORED_BROWSER_ERROR_MESSAGES.some((ignoredMessage) => normalizedMessage.includes(ignoredMessage));
};

const ErrorBoundary = ({ children }: ErrorBoundaryProps) => {
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [errorInfo, setErrorInfo] = useState<React.ErrorInfo | undefined>(undefined);

  useEffect(() => {
    const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
      console.error("Error caught in ErrorBoundary: ", error, errorInfo);
      setError(error);
      setErrorInfo(errorInfo);
      setHasError(true);
    };

    const handleWindowError = (event: ErrorEvent) => {
      if (isIgnoredBrowserError(event.message)) {
        event.preventDefault();
        return;
      }
      handleError(new Error(event.message), { componentStack: event.filename + ':' + event.lineno + ':' + event.colno } as React.ErrorInfo);
    };

    const handlePromiseRejection = (event: PromiseRejectionEvent) => {
      if (isIgnoredBrowserError(event.reason)) {
        event.preventDefault();
        return;
      }
      handleError(new Error(event.reason), { componentStack: '' } as React.ErrorInfo);
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handlePromiseRejection);

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
    };
  }, []);

  if (hasError) {
    return (
      <div data-testid="app-error-boundary">
        <h1>Something went wrong.</h1>
        <details style={{ whiteSpace: 'pre-wrap' }}>
          {error && error.toString()}
          <br />
          {errorInfo?.componentStack}
        </details>
      </div>
    );
  }

  return <>{children}</>;
};

export default ErrorBoundary;
